import { Outlet, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Mail, 
  UserCheck, 
  LogOut,
  Menu,
  X,
  Briefcase,
  Ticket,
  UserCog,
  FolderKanban,
  Receipt,
  UserPlus,
  MessageSquare,
  ListTodo,
  MessagesSquare,
  TrendingUp,
  Instagram,
  Linkedin,
  Handshake,
  Megaphone,
  Calendar,
  Link as LinkIcon
} from 'lucide-react';
import { Button } from './ui/button';

const LOGO_URL = "https://static.prod-images.emergentagent.com/jobs/4e491698-f834-49a8-a1e5-8fd3efa88feb/images/d77c0364065b8ec483b9e6915ff1d30475de2851aefe5560f16a14318cc351be.png";

const navItems = [
  { label: 'Dashboard', href: '/admin', icon: LayoutDashboard },
  { type: 'divider', label: 'Direction Commerciale' },
  { label: 'Dashboard Commercial', href: '/admin/commercial', icon: TrendingUp },
  { label: 'Instagram', href: '/admin/commercial/pipeline/instagram', icon: Instagram },
  { label: 'LinkedIn', href: '/admin/commercial/pipeline/linkedin', icon: Linkedin },
  { label: 'Partenaires', href: '/admin/commercial/pipeline/partenaires', icon: Handshake },
  { label: 'Influenceurs', href: '/admin/commercial/pipeline/influenceurs', icon: Megaphone },
  { label: 'Salons', href: '/admin/commercial/pipeline/salons', icon: Calendar },
  { label: 'Affiliation', href: '/admin/commercial/pipeline/affiliation', icon: LinkIcon },
  { type: 'divider', label: 'CRM BatiAssist' },
  { label: 'Prospects', href: '/admin/crm/prospects', icon: Users },
  { label: 'Pipeline', href: '/admin/crm/pipeline', icon: FolderKanban },
  { label: 'Clients', href: '/admin/crm/clients', icon: Briefcase },
  { label: 'Devis', href: '/admin/crm/quotes', icon: FileText },
  { label: 'Factures', href: '/admin/crm/invoices', icon: Receipt },
  { label: 'Tickets Support', href: '/admin/crm/tickets', icon: Ticket },
  { type: 'divider', label: 'Ressources' },
  { label: 'Candidats', href: '/admin/resources/candidates', icon: UserPlus },
  { label: 'Assistants', href: '/admin/resources/assistants', icon: UserCog },
  { label: 'Tchat Clients', href: '/admin/resources/client-chat', icon: MessagesSquare },
  { label: 'Tchat équipe', href: '/admin/resources/chat', icon: MessageSquare },
  { label: 'Tâches', href: '/admin/resources/tasks', icon: ListTodo },
  { type: 'divider', label: 'Site Web' },
  { label: 'Articles', href: '/admin/blog', icon: FileText },
  { label: 'Contacts', href: '/admin/contacts', icon: Mail },
  { label: 'Newsletter', href: '/admin/newsletter', icon: Users },
  { label: 'Leads', href: '/admin/leads', icon: UserCheck },
  { type: 'divider', label: 'Monétisation' },
  { label: 'Publicités', href: '/admin/ads', icon: Megaphone },
];

export default function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('admin_token');

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  // Redirect if not authenticated
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Mobile overlay */}
      {isSidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`fixed lg:static inset-y-0 left-0 z-50 w-[280px] sm:w-64 bg-rm-primary text-white transform transition-transform duration-300 ease-in-out ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-4 sm:p-6 border-b border-white/10 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3">
              <img 
                src={LOGO_URL} 
                alt="BatiAssist" 
                className="h-7 sm:h-8 w-auto brightness-0 invert"
              />
              <span className="font-semibold text-sm sm:text-base">Admin</span>
            </Link>
            <button
              className="lg:hidden p-2 hover:bg-white/10 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center"
              onClick={() => setIsSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-2 sm:p-4 space-y-0.5 sm:space-y-1 overflow-y-auto">
            {navItems.map((item, index) => {
              if (item.type === 'divider') {
                return (
                  <div key={`divider-${index}`} className="pt-3 sm:pt-4 pb-1 sm:pb-2">
                    <span className="px-3 sm:px-4 text-[10px] sm:text-xs font-semibold text-white/50 uppercase tracking-wider">
                      {item.label}
                    </span>
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                              (item.href !== '/admin' && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-2.5 rounded-lg transition-colors min-h-[44px] ${
                    isActive 
                      ? 'bg-white/20 text-white' 
                      : 'text-white/70 hover:bg-white/10 hover:text-white active:bg-white/20'
                  }`}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                  <span className="text-sm sm:text-base">{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Logout */}
          <div className="p-3 sm:p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-3 w-full text-white/70 hover:bg-white/10 hover:text-white active:bg-white/20 rounded-lg transition-colors min-h-[48px]"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-sm sm:text-base">Deconnexion</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top Bar */}
        <header className="bg-white border-b border-slate-200 px-3 sm:px-4 lg:px-8 py-3 sm:py-4 flex items-center justify-between sticky top-0 z-30">
          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 min-h-[44px] min-w-[44px] flex items-center justify-center"
            onClick={() => setIsSidebarOpen(true)}
            data-testid="admin-menu-toggle"
          >
            <Menu className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <div className="flex-1" />
          <Link to="/" className="text-xs sm:text-sm text-slate-600 hover:text-rm-primary px-2 py-1">
            Voir le site
          </Link>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-2 sm:p-4 lg:p-8 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
